import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BuildingSummary, GroupTag } from '../types/protocols';
import { getBuildingTags } from '../api/buildingTagsApi';
import { useBuildingsData } from '../Helpers/BuildingSummariesContext';
import { loadFromInstanceStorage, saveToInstanceStorage } from '../Helpers/instanceStorage';
import { isUntagged, UNTAGGED_OPTION_VALUE } from './mapOverview/buildingSort';

/**
 * Load the persisted deselection, tolerating corrupt/legacy storage values —
 * anything that isn't a string array falls back to "nothing deselected".
 */
function loadDeselectedFromStorage(storageKey: string): string[] {
    const stored = loadFromInstanceStorage<unknown>(storageKey, []);
    if (!Array.isArray(stored)) return [];
    return stored.filter((key): key is string => typeof key === 'string');
}

/**
 * Building group tag (byggnadsgrupp) filter for list/board pages.
 *
 * Selectable options are each tag plus "Utan grupp" (UNTAGGED_OPTION_VALUE)
 * for buildings without any tag. The DESELECTED options are what gets
 * persisted (under `storageKey`), so tags created after the user last touched
 * the filter default to selected instead of silently hiding their buildings.
 * Deselections of since-deleted tags are ignored rather than eagerly pruned.
 */
export function useBuildingTagFilter(storageKey: string) {
    const { buildings } = useBuildingsData();

    const [availableTags, setAvailableTags] = useState<GroupTag[]>([]);
    useEffect(() => {
        getBuildingTags().then(setAvailableTags);
    }, []);

    const [deselectedTagKeys, setDeselectedTagKeys] = useState<string[]>(
        () => loadDeselectedFromStorage(storageKey)
    );

    // Persist on change; skip the initial mount (nothing changed yet)
    const isFirstRun = useRef(true);
    useEffect(() => {
        if (isFirstRun.current) {
            isFirstRun.current = false;
            return;
        }
        saveToInstanceStorage(storageKey, deselectedTagKeys);
    }, [storageKey, deselectedTagKeys]);

    // All selectable option keys ("Utan grupp" + each tag)
    const tagOptionKeys = useMemo(
        () => [UNTAGGED_OPTION_VALUE, ...availableTags.map(t => t.key)],
        [availableTags]
    );

    // Deselections restricted to options that still exist
    const effectiveDeselected = useMemo(() => {
        const options = new Set(tagOptionKeys);
        return new Set(deselectedTagKeys.filter(key => options.has(key)));
    }, [deselectedTagKeys, tagOptionKeys]);

    // Tag keys (excluding the untagged sentinel) currently selected
    const selectedTagKeySet = useMemo(() => {
        const set = new Set(availableTags.map(t => t.key));
        effectiveDeselected.forEach(key => set.delete(key));
        return set;
    }, [availableTags, effectiveDeselected]);

    const tagOptionCount = tagOptionKeys.length;
    const selectedTagOptionCount = tagOptionCount - effectiveDeselected.size;
    const tagFilterActive = availableTags.length > 0 && effectiveDeselected.size > 0;
    const allTagOptionsSelected = effectiveDeselected.size === 0;

    const isTagOptionSelected = useCallback(
        (key: string): boolean => !effectiveDeselected.has(key),
        [effectiveDeselected]
    );

    const buildingsByKey = useMemo(() => {
        const map = new Map<string, BuildingSummary>();
        (buildings ?? []).forEach(building => {
            if (building.key) {
                map.set(building.key, building);
            }
        });
        return map;
    }, [buildings]);

    // Whether the given building passes the tag filter
    const matchesBuildingTagFilter = useCallback((buildingKey: string): boolean => {
        // No tags defined or nothing deselected: filter inactive
        if (availableTags.length === 0 || effectiveDeselected.size === 0) {
            return true;
        }
        // Everything deselected: show nothing
        if (selectedTagOptionCount === 0) {
            return false;
        }
        const building = buildingsByKey.get(buildingKey);
        // If building unknown, include it (don't hide due to missing data)
        if (!building) {
            return true;
        }
        if (isUntagged(building)) {
            return !effectiveDeselected.has(UNTAGGED_OPTION_VALUE);
        }
        return Object.keys(building.groupTags ?? {}).some(key => selectedTagKeySet.has(key));
    }, [availableTags.length, effectiveDeselected, selectedTagOptionCount, buildingsByKey, selectedTagKeySet]);

    // Toggle a single option (a tag key or UNTAGGED_OPTION_VALUE)
    const toggleTagOption = useCallback((key: string) => {
        setDeselectedTagKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    }, []);

    // Select all options when any is deselected, otherwise deselect all
    const toggleAllTagOptions = useCallback(() => {
        setDeselectedTagKeys(effectiveDeselected.size === 0 ? [...tagOptionKeys] : []);
    }, [effectiveDeselected, tagOptionKeys]);

    return {
        availableTags,
        tagOptionCount,
        selectedTagOptionCount,
        tagFilterActive,
        allTagOptionsSelected,
        isTagOptionSelected,
        toggleTagOption,
        toggleAllTagOptions,
        matchesBuildingTagFilter,
    };
}
